import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Smartphone, X, Chrome, Globe } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Auto-show the dialog after a short delay
      setTimeout(() => {
        setShowInstallDialog(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show install prompt after page load
    if (iOS && !isInstalled) {
      setTimeout(() => {
        setShowInstallDialog(true);
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome installation
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          setDeferredPrompt(null);
          setShowInstallDialog(false);
        }
      } catch (error) {
        console.error('Installation failed:', error);
      }
    } else if (isIOS) {
      // iOS instructions are shown in the dialog
      return;
    }
  };

  const dismissPrompt = () => {
    setShowInstallDialog(false);
    // Don't show again for 24 hours
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if recently dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < dayInMs) {
        return;
      }
    }
  }, []);

  if (isInstalled || (!deferredPrompt && !isIOS)) {
    return null;
  }

  return (
    <>
      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Install Sortable App
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center">
              <Download className="h-12 w-12 text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">
                Get faster access to Sortable! Install our app for a better experience with push notifications and offline access.
              </p>
            </div>

            {isIOS ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Install on iOS
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">1</span>
                    <p>Tap the Share button <span className="font-mono">⎋</span> at the bottom of your screen</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">2</span>
                    <p>Scroll down and tap "Add to Home Screen"</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">3</span>
                    <p>Tap "Add" to install Sortable on your home screen</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="flex gap-2">
                <Button onClick={handleInstallClick} className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Install App
                </Button>
              </div>
            )}

            <div className="flex justify-between items-center pt-2">
              <Button variant="ghost" size="sm" onClick={dismissPrompt}>
                <X className="h-3 w-3 mr-1" />
                Not now
              </Button>
              {!isIOS && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Chrome className="h-3 w-3" />
                  Works best in Chrome
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating install button */}
      {!showInstallDialog && (
        <Button
          onClick={() => setShowInstallDialog(true)}
          className="fixed bottom-6 left-6 rounded-full shadow-lg z-50"
          size="sm"
        >
          <Download className="h-3 w-3" />
        </Button>
      )}
    </>
  );
};